/**
 * What every Sukha Sentry client may collect — one answer for web (client,
 * server, edge), the API, realtime and the extension (TX-5459).
 *
 * Sentry 11 replaced `sendDefaultPii` with `dataCollection`, and its defaults
 * switch on far more than 10 sent: `user.*`, cookies, every HTTP request and
 * response body, generative-AI inputs and outputs, database query data and
 * local variables in stack frames. No init here ever set `sendDefaultPii`, so
 * on 10 none of that left the process. This keeps it that way, explicitly.
 *
 * What stays on is what triage needs: request headers, minus credentials and
 * the member's IP. URL query parameters are OFF, unlike 10: signed links (the
 * chart routes' `?token=`, invite and accept-token links) carry their
 * credential in the query string, on web as well as the API. The API's span
 * scrub stays as a second layer.
 *
 * Typed locally rather than with `@sentry/core`'s `DataCollection`, so this
 * package keeps no Sentry dependency; each init site checks it against the
 * real type when it passes it in.
 */

type CollectBehavior = boolean | { allow: string[] } | { deny: string[] };
type HttpBodyCollectionTarget = "incomingRequest" | "outgoingRequest" | "incomingResponse" | "outgoingResponse";

export interface SukhaSentryDataCollection {
  userInfo: boolean;
  cookies: CollectBehavior;
  httpHeaders: { request: CollectBehavior; response: CollectBehavior };
  httpBodies: HttpBodyCollectionTarget[];
  urlQueryParams: CollectBehavior;
  graphQL: { document: boolean; variables: boolean };
  genAI: { inputs: boolean; outputs: boolean };
  mcp: { inputs: boolean; outputs: boolean };
  databaseQueryData: boolean;
  queues: boolean;
  stackFrameVariables: boolean;
}

/**
 * Request headers never sent: credentials, the member's IP, and Vercel's
 * geolocation of them.
 *
 * Sentry matches these as case-insensitive SUBSTRINGS of the header name, so
 * a prefix covers a family: `x-vercel-ip-` is the city, region, latitude,
 * longitude and postal code Vercel adds to every request, and `x-forwarded-`
 * is every proxy header (Sentry 10 kept all of them out).
 */
export const SENSITIVE_REQUEST_HEADERS = [
  "cookie",
  "authorization",
  "proxy-authorization",
  "x-api-key",
  "x-forwarded-",
  "x-real-ip",
  "x-vercel-forwarded-for",
  "x-vercel-ip-",
  "x-vercel-proxied-for",
  "cf-connecting-ip",
  "true-client-ip",
  "forwarded",
] as const;

/**
 * The privacy options to spread into every `Sentry.init` / client.
 *
 * A function, not a constant, so no caller can mutate what another receives.
 * `attachStacktrace: false` keeps 10's behaviour for `captureMessage`: 11
 * attaches a synthetic stack by default, which would regroup every existing
 * message issue by call site.
 */
export function sukhaSentryPrivacyOptions(): {
  dataCollection: SukhaSentryDataCollection;
  attachStacktrace: false;
} {
  return {
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: { request: { deny: [...SENSITIVE_REQUEST_HEADERS] }, response: false },
      httpBodies: [],
      urlQueryParams: false,
      graphQL: { document: false, variables: false },
      genAI: { inputs: false, outputs: false },
      mcp: { inputs: false, outputs: false },
      databaseQueryData: false,
      queues: false,
      stackFrameVariables: false,
    },
    attachStacktrace: false,
  };
}
